import { useState } from "react";

import { api, ApiError, type GroupStrayMatch } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { strayMatchesConfirmText, strayMatchesHeadline, strayMatchLine } from "../lib/rrRebuild";

interface GroupStrayMatchesNoticeProps {
  token: string;
  tournamentId: number;
  groupId: number;
  groupName: string;
  /** `ghost_matches` del listado de grupos. */
  matches: GroupStrayMatch[] | null | undefined;
  /** Sin permiso de editar grupos: solo el aviso, sin botón. */
  canEdit?: boolean;
  /** Ya se cancelaron: mensaje del servidor; quien llama recarga. */
  onDone: (message: string) => void | Promise<void>;
  onError?: (message: string) => void;
}

/**
 * Partidos que sobran en un grupo (torneo 71, Eduardo 2026-10-13): de
 * jugadores que ya no están en él (cambio de categoría, «Quitar», el «Mover»
 * viejo) o repetidos (la misma pareja dos veces). Siguen como «por jugar» y
 * nunca se van a jugar. «Quitar esos partidos» los cancela con su motivo
 * después de confirmar la lista; lo jugado no se toca.
 */
export default function GroupStrayMatchesNotice({
  token,
  tournamentId,
  groupId,
  groupName,
  matches,
  canEdit = true,
  onDone,
  onError,
}: GroupStrayMatchesNoticeProps) {
  const [busy, setBusy] = useState(false);
  const { askConfirm } = useAbortableConfirm([tournamentId, groupId]);
  const list = matches ?? [];
  if (list.length === 0) return null;

  async function handleClick() {
    const accepted = await askConfirm(strayMatchesConfirmText(groupName, list), {
      acceptLabel: list.length === 1 ? "Sí, quitar ese partido" : "Sí, quitar esos partidos",
      tone: "danger",
    });
    if (!accepted) return;
    setBusy(true);
    try {
      const response = await api.groups.cancelGhostMatches(
        token,
        tournamentId,
        groupId,
        list.map((match) => match.match_id),
      );
      await onDone(response.data.message);
    } catch (err) {
      const text =
        err instanceof ApiError
          ? err.message || "No se pudieron quitar esos partidos."
          : "Error de red. Intenta otra vez.";
      onError?.(text);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rr-stray-notice" role="status" data-group-id={groupId}>
      <p>
        <strong>{strayMatchesHeadline(list)}</strong> Siguen como «por jugar» y no se van a jugar.
      </p>
      <ul className="rr-stray-notice__list">
        {list.map((match) => (
          <li key={match.match_id}>{strayMatchLine(match)}</li>
        ))}
      </ul>
      {canEdit ? (
        <button
          type="button"
          className="btn btn--small rr-stray-notice__button"
          onClick={() => void handleClick()}
          disabled={busy}
        >
          {busy ? "Quitando…" : "Quitar esos partidos"}
        </button>
      ) : null}
    </div>
  );
}
